import _ from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type { ReactDatePickerProps } from 'react-datepicker';
import { Button } from 'src/@vymo/ui/atoms';
import BaseDateTime from '../base';
import { DATE_TIME_FORMAT } from '../constants';
import TimePicker24Hour from '../timePicker/timePicker24Hour';
import { DatePickerProps } from './types';
import baseStyles from '../index.module.scss';
import styles from './index.module.scss';

const DatePickerWithTime = forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<
    Omit<ReactDatePickerProps, 'value' | 'onChange'> & DatePickerProps
  >
>(
  (
    {
      value,
      onChange = _.noop,
      format,
      discreteTimeOptions = { hours: null, minutes: null, seconds: null },
      timeStep = {},
      'data-test-id': dataTestId,
      viewMode = false,
      showPopperArrow = false,
      calendarPhase,
      ...datepickerProps
    },
    ref = null,
  ) => {
    const datePickerRef = useRef(null);

    const [okButtonDisable, setOkButtonDisable] = useState(!value);

    useEffect(() => {
      setOkButtonDisable(!value);
    }, [value]);

    useImperativeHandle(ref, () => Object.assign(datePickerRef.current as any));

    format = format || DATE_TIME_FORMAT;

    const onChangeBaseDatePicker = useCallback(
      (arg0, event) => {
        onChange(arg0, event);
        setOkButtonDisable(false);
      },
      [onChange],
    );

    const phaseLabels: Record<'start' | 'end', string> = {
      start: 'Start time',
      end: 'End time',
    };
    const phaseLabel = calendarPhase ? phaseLabels[calendarPhase] : null;

    const getDateWithTimeFooter = useCallback(
      () => (
        <div className={styles.dateWithTimePicker__footer}>
          {phaseLabel && (
            <span className={styles.dateWithTimePicker__phaseLabel}>
              {phaseLabel}
            </span>
          )}
          <Button
            disabled={okButtonDisable}
            onClick={(event) => {
              event.stopPropagation();
              if (datePickerRef.current) {
                // @ts-ignore
                datePickerRef.current.commitSelection?.();
                // @ts-ignore
                datePickerRef.current.setIsOpen(false);
              }
            }}
          >
            {calendarPhase === 'start' ? 'Next' : 'Ok'}
          </Button>
        </div>
      ),
      [calendarPhase, okButtonDisable, phaseLabel],
    );

    return (
      <BaseDateTime
        ref={datePickerRef}
        value={value}
        onChange={onChangeBaseDatePicker}
        format={format}
        showTimeInput
        popperClassName={`${baseStyles.popper} ${styles.dateWithTimePicker}`}
        customTimeInput={
          //  @ts-ignore
          <TimePicker24Hour
            discreteTimeOptions={discreteTimeOptions}
            format={format as any}
            step={timeStep}
          />
        }
        showPopperArrow={showPopperArrow}
        shouldCloseOnSelect={false}
        todayButton={getDateWithTimeFooter()}
        data-test-id={dataTestId}
        viewMode={viewMode}
        {...datepickerProps}
      />
    );
  },
);

export default DatePickerWithTime;
